import React from 'react'
import PropTypes from 'prop-types'

/* doc
---
title: Column
name: Column
category: React - Bulma
parent: Columns
---

A `Column` component based on Bulma `.column`. This should be used nested inside
a `Columns` component. Component will not render if there are no children.

See [Bulma Documentation](http://bulma.io/documentation/columns/basics/) for reference and full options.
If one of the Bulma options isn't listed below, it may be added using the `className` prop.

### Props
| Prop Name       | Description                                 |Type            | Default Value             | Required?     |
|-----------------|---------------------------------------------|----------------|---------------------------|---------------|
| isNarrow        | Column only takes up space it needs to.     | boolean        | false                     | no            |
| size            | Column size. eg) "1" or "two-thirds"        | string         | ""                        | no            |
| offset          | Column offset. eg) "1" or "one-quarter"     | string         | ""                        | no            |


```html_example
  <Columns isMobile>
    <Column size="two-thirds">
      ...
    </Column>
    <Column isNarrow>
      ...
    </Column>
  </Columns>
```

The props currently only support one possible size and offset value. If you need to get
more advanced with multiple column sizes (eg: mobile/tablet specific sizing), you can
add the additional styles with just a `className` prop following the Bulma guides.

```html_example
  <Columns isMobile>
    <Column size="two-thirds" className="is-half-mobile">
      ...
    </Column>
  </Columns>
```
*/
function Column(props) {
  const {
    children, className, isNarrow, size, offset, ...rest
  } = props

  if (!children) return false

  const classes = [
    'column',
    className || '',
    isNarrow ? 'is-narrow' : '',
    size ? `is-${size}` : '',
    offset ? `is-offset-${offset}` : '',
  ]

  return (
    <div {...rest} className={classes.join(' ')}>
      {children}
    </div>
  )
}

Column.defaultProps = {
  isNarrow: false,
  size: '',
  offset: '',
}

Column.propTypes = {
  // Columns should begin at mobile.
  isMobile: PropTypes.bool,
  // Columns should begin at tablet.
  isTablet: PropTypes.bool,
  // Columns should begin at desktop.
  isDesktop: PropTypes.bool,
  // Column only takes up the space it needs.
  isNarrow: PropTypes.bool,
  // Size of column. eg: "6", "one-quarter", etc.
  size: PropTypes.string,
  // Offset of column. eg: "6", "one-quarter", etc.
  offset: PropTypes.string,
}

export default Column
